import React from "react";

// ── Platform brand icons (inline SVG, no deps) ────────────────────────────────
function FbIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#1877F2">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );
}

function XIcon({ size = 14, color = "#ffffff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );
}

function IgIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <defs>
        <linearGradient id="ig-grad-pub" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#F58529" />
          <stop offset="50%" stopColor="#DD2A7B" />
          <stop offset="100%" stopColor="#8134AF" />
        </linearGradient>
      </defs>
      <path fill="url(#ig-grad-pub)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  );
}

// ── Single metric card with sparkline ────────────────────────────────────────
function MetricCard({ label, value, loading, color, theme: t }) {
  return (
    <div style={{
      background: t.surface2, border: `1px solid ${t.border}`,
      borderRadius: 12, padding: "12px 12px 10px",
      flex: 1, minWidth: 0,
      display: "flex", flexDirection: "column", gap: 3,
    }}>
      <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 900, color: value != null ? color : t.textFaint, fontFamily: "Georgia, serif", lineHeight: 1.1 }}>
        {loading
          ? <span style={{ fontSize: 11, color: t.textFaint }}>…</span>
          : value != null ? value.toLocaleString() : <span style={{ fontSize: 18, color: t.textFaint }}>–</span>
        }
      </div>
    </div>
  );
}

// ── A platform section (Facebook / Twitter / Instagram) ───────────────────────
function PlatformSection({ icon, name, color, configured, loading, metrics, theme: t }) {
  return (
    <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 14, marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          {icon}
          <span style={{ fontSize: 10, fontWeight: 800, color: t.textSub, textTransform: "uppercase", letterSpacing: 1 }}>{name}</span>
        </div>
        {loading
          ? <span style={{ fontSize: 9, color: t.textFaint }}>Connecting…</span>
          : configured
            ? <span style={{ fontSize: 9, color: "#22c55e", background: "#22c55e11", padding: "2px 8px", borderRadius: 20, border: "1px solid #22c55e33", fontWeight: 700 }}>● Live</span>
            : <span style={{ fontSize: 9, color: t.textFaint, background: t.surface2, padding: "2px 8px", borderRadius: 20, border: `1px solid ${t.border}` }}>Not Connected</span>
        }
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {metrics.map(m => (
          <MetricCard key={m.label} label={m.label} value={m.value} loading={loading && !configured} color={color} theme={t} />
        ))}
      </div>
    </div>
  );
}

// ── Main card ─────────────────────────────────────────────────────────────────
export function PublicationSocialCard({ pub, realtimeEntry, fbStats = null, igStats = null, theme: t }) {
  const {
    name        = "Unknown",
    color       = "#2563EB",
    platforms   = {},
    totalFollowers   = 0,
    configuredPlatforms = 0,
  } = pub;

  // Resolve Facebook data
  const fbConfigured = fbStats?.configured === true;
  const fbLoading    = !fbConfigured && (fbStats?.loading === true);
  const fbFollowers  = fbConfigured ? (fbStats.followersCount ?? null) : null;
  const fbFollowing  = fbConfigured ? (fbStats.followingCount ?? null) : null;
  const fbPosts      = fbConfigured ? (fbStats.postsCount     ?? null) : null;

  // Resolve Instagram data (NewsDay only)
  const showIg    = pub.key === "newsday";
  const igConfigured = igStats?.configured === true;
  const igLoading    = !igConfigured && (igStats?.loading === true);
  const igFollowers  = igConfigured ? (igStats.followersCount ?? null) : null;
  const igPosts      = igConfigured ? (igStats.mediaCount    ?? null) : null;
  const igFollowing  = igConfigured ? (igStats.followsCount  ?? null) : null;

  // Resolve Twitter data
  const twData       = platforms.twitter ?? {};
  const twConfigured = twData.configured === true;

  // Status count (count FB from live hook, not stale platforms object)
  const effectiveConfigured = configuredPlatforms
    + (fbConfigured && !(platforms.facebook?.configured) ? 1 : 0)
    + (showIg && igConfigured && !(platforms.instagram?.configured) ? 1 : 0);
  const maxPlatforms = showIg ? 3 : 2;
  const allUnconfigured = effectiveConfigured === 0;

  const xColor = t.name === "dark" ? "#e2e8f0" : "#0f172a";

  return (
    <div style={{
      background: t.surface,
      border: `1px solid ${allUnconfigured ? t.border : color + "33"}`,
      borderRadius: 16,
      padding: "16px 18px",
    }}>
      {/* Publication header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
          <span style={{ fontSize: 14, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>{name}</span>
        </div>
        {effectiveConfigured > 0
          ? <span style={{ fontSize: 9, color: "#22c55e", background: "#22c55e11", padding: "3px 8px", borderRadius: 20, border: "1px solid #22c55e33", fontWeight: 700 }}>
              {effectiveConfigured}/{maxPlatforms} live
            </span>
          : <span style={{ fontSize: 9, color: t.textFaint, background: t.surface2, padding: "3px 8px", borderRadius: 20, border: `1px solid ${t.border}` }}>
              Not Connected
            </span>
        }
      </div>

      {/* Total followers display */}
      {totalFollowers > 0 && (
        <div style={{ marginBottom: 4 }}>
          <span style={{ fontSize: 22, fontWeight: 900, color, fontFamily: "Georgia, serif" }}>{totalFollowers.toLocaleString()}</span>
          <span style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginLeft: 6 }}>Total Followers</span>
        </div>
      )}

      {/* Facebook section */}
      <PlatformSection
        icon={<FbIcon size={15} />}
        name="Facebook"
        color="#1877F2"
        configured={fbConfigured}
        loading={fbLoading}
        metrics={[
          { label: "Followers", value: fbFollowers },
          { label: "Following", value: fbFollowing },
          { label: "Posts",     value: fbPosts },
        ]}
        theme={t}
      />

      {/* Twitter/X section */}
      <PlatformSection
        icon={<XIcon size={13} color={xColor} />}
        name="Twitter / X"
        color={xColor}
        configured={twConfigured}
        loading={false}
        metrics={[
          { label: "Followers", value: twConfigured ? (twData.followers ?? null) : null },
          { label: "Following", value: twConfigured ? (twData.following ?? null) : null },
          { label: "Tweets",    value: twConfigured ? (twData.tweetCount ?? null) : null },
        ]}
        theme={t}
      />

      {/* Instagram section — NewsDay only */}
      {showIg && (
        <PlatformSection
          icon={<IgIcon size={15} />}
          name="Instagram"
          color="#E1306C"
          configured={igConfigured}
          loading={igLoading}
          metrics={[
            { label: "Followers", value: igFollowers },
            { label: "Posts",     value: igPosts },
            { label: "Following", value: igFollowing },
          ]}
          theme={t}
        />
      )}
    </div>
  );
}
